// [PURPOSE]: Pure helper that builds the (sourceId, effects) tuples
//   for currently-equipped items in the canonical inventory array.
//   Single source of truth for the re-registration pass that runs
//   on every inventory mutation in InventoryContext.
//
// [WHY]: Extracted to its own module (Batch 14 / P0 C-8) so the
//   regression guard at __tests__/itemEffectsReregisterOnReload
//   can exercise the SAME logic the production useEffect calls.
//   Pre-fix the test had its own inline `buildItemRegistrationsFromInventory`
//   helper that fabricated the contract — passing tests no longer
//   meant production was correct.
//
// [INTEGRATION]: InventoryContext.js imports this and runs the
//   useEffect that walks inventory, gets the registration tuples,
//   and registers each via effectRegistry.register(sourceId, effects,
//   metadata). Idempotent — register removes any prior entry under
//   the same sourceId before inserting.

/**
 * Compute the (sourceId, effects) tuples for currently-equipped items
 * in the canonical inventory array. Mirrors the contract that
 * InventoryContext.equipItem uses on the equip-event path so the
 * reload path matches.
 *
 * [SUB-PLAN 1H-F — 2026-05-28]: ADDITIVE FIX — also registers item.itemEffects
 *   (triggered effects) that were previously skipped.
 *
 *   Two registration paths:
 *   1. EQUIPPED items (item.equipped): register BOTH passive effects (item.effects)
 *      AND triggered effects (item.itemEffects). Combined array prevents the
 *      need for callers to know which field a particular effect lives in.
 *   2. AMMO items (item.ammoType set, NOT equipped — ammo has no equip slot):
 *      register ONLY the triggered effects (item.itemEffects). The
 *      `condition.ammoTypeMatch` on each triggered effect (verified against
 *      effectCalculator._triggeredEffectMatches L188 + EquipmentPanel.js L436-447
 *      attack context) ensures ONLY the ammo matching the weapon's ammoType
 *      fires per attack — registering all inventory ammo is safe and correct.
 *
 *   AMMO-GATE DECISION (confirmed by reading the attack path, NOT guessed):
 *   - EquipmentPanel.js:436 passes `ammo: consumedAmmoItem` in eventContext.
 *   - effectCalculator._readTriggerContextField (L158) maps `ammoTypeMatch` →
 *     `eventContext.ammo?.ammoType`.
 *   - _triggeredEffectMatches (L188) does case-insensitive string comparison
 *     against condition.ammoTypeMatch.
 *   - Result: two ammo types in inventory (arrows + crossbow_bolts) both
 *     registered; only the one whose ammoTypeMatch equals the fired weapon's
 *     ammoType applies. Over-fire is impossible.
 *
 * @param {Array} inventory
 * @returns {Array<{ sourceId: string, effects: Array, item: Object }>}
 */
export function buildItemRegistrationsFromInventory(inventory) {
  if (!Array.isArray(inventory)) return [];
  const out = [];
  for (const item of inventory) {
    if (!item) continue;
    const passive = Array.isArray(item.effects) ? item.effects : [];
    const triggered = Array.isArray(item.itemEffects) ? item.itemEffects : [];

    if (item.equipped && (!item.requiresAttunement || item.attuned)) {
      // [T1 — AC-bug fix 2026-05-31]: Attunement gate. DMG p.136: attunement-requiring
      // items only grant benefits when attuned. Previously the guard was missing; an
      // equipped-but-not-attuned Cloak of Protection would silently apply its +1 AC and
      // save bonuses. The dead legacy path at effectCalculator.js:956 had this guard;
      // the modern bridge path never got it. Restores correct RAW behaviour.
      // Equipped gear: register passive + triggered combined.
      // This covers weapons (Flame Tongue on_hit), armor, rings, etc.
      const combined = [...passive, ...triggered];
      if (combined.length === 0) continue;
      const slot = item.equipSlot || 'unknown';
      out.push({
        sourceId: `item:${item.id}:${slot}`,
        effects: combined,
        item,
      });
    } else if (item.ammoType && triggered.length > 0) {
      // Ammo in inventory (not equipped — ammo has no equip slot).
      // Register ONLY triggered effects; passive effects on unequipped
      // items should not apply. The ammoTypeMatch condition gates which
      // ammo applies per attack — registering all inventory ammo is safe.
      out.push({
        sourceId: `item:${item.id}:ammo`,
        effects: triggered,
        item,
      });
    }
  }
  return out;
}

/**
 * [STONE 6 / #70 — 2026-05-18]: buildEffectsFromForm — converts a flat
 *   catalog-creator form state into the registry-compatible effects array
 *   that downstream `effectRegistry.register(sourceId, effects, ...)` reads.
 *
 * [WHY]: CustomItemCreator's form has structured fields (resistances[],
 *   immunities[], conditionImmunities[], senses {darkvision, blindsight,
 *   tremorsense}, speedBonus / flyingSpeed / swimmingSpeed, statBonus +
 *   statValue). These must round-trip through the same effect-row shape
 *   the runtime registers — otherwise the catalog item authored via
 *   Workshop fails the cardinal Rule 12 contract (CW content must work
 *   like SRD content). Per Rule 22 the assembly logic lives here, not
 *   inlined in the creator — both production registration paths and the
 *   creator's save handler import this single source of truth.
 *
 * [SHAPES]: Conforms to effectRegistry's UNIVERSAL_EFFECT_SCHEMA:
 *   - resistance grant:  { type: 'resistance', target: <dmgType>, operation: 'grant' }
 *   - immunity:          { type: 'resistance', target: <dmgType>, operation: 'immunity' }
 *   - sense grant:       { type: 'sense', target: 'darkvision'|..., value: <ft>, operation: 'grant' }
 *   - speed bonus:       { type: 'speed', target: 'walk'|'fly'|'swim', operation: 'add', value: <ft> }
 *   - stat modification: { type: 'ability', target: 'str'|'dex'|...|'all', operation: 'add'|'set', value: <n> }
 *   - condition immunity: { type: 'conditionImmunity', target: <conditionName>, operation: 'grant' }
 *   - hp bonus:          { type: 'hp', operation: 'add', value: <n> }
 *   - ac bonus:          { type: 'ac', operation: 'add', value: <n> }
 *   - initiative bonus:  { type: 'initiative', operation: 'add', value: <n> }
 *   - save bonus:        { type: 'save:<ability>', operation: 'add', value: <n> }
 *                        e.g. { type: 'save:wisdom', operation: 'add', value: 1 }
 *                        'all' expands to 6 per-ability rows at emit time.
 *
 * [RULE 22]: Single source of truth. The Workshop creator MUST NOT
 *   reimplement this assembly logic inline.
 *
 * @param {Object} form - flat form state from CustomItemCreator
 * @returns {Array} effects rows (may be empty)
 */
export function buildEffectsFromForm(form) {
  if (!form || typeof form !== 'object') return [];
  const effects = [];

  // Resistances → resistance.grant rows.
  // [HOTFIX 2026-05-26]: Schema convention for resistance is `value: <damageType>`,
  //   NOT target. The reader at effectCalculator.getDamageResistances (L2967-2970)
  //   queries effect.value; effectRegistry's per-type contract in the docstring
  //   block (effectRegistry.js L44) is authoritative: "resistance: Grant damage
  //   resistance/immunity (value: damage type; operation: 'grant'|'immunity')."
  //   Pre-hotfix this helper emitted `target:` and the Stones 5-6 dispatch shipped
  //   that shape into both dialogs, causing user-authored resistances to be
  //   silently unreadable at runtime. The matching test contract (Rule 31 trace
  //   section f) now requires a register-and-read data-flow assertion, not just
  //   a shape assertion, to prevent recurrence.
  if (Array.isArray(form.resistances)) {
    for (const dmgType of form.resistances) {
      if (dmgType) effects.push({ type: 'resistance', value: String(dmgType), operation: 'grant' });
    }
  }

  // Immunities → resistance.immunity rows (same type, distinct operation —
  // the existing getDamageResistances + getDamageImmunities consumers read both
  // via the same effectRegistry stat key 'resistance'; both query effect.value).
  if (Array.isArray(form.immunities)) {
    for (const dmgType of form.immunities) {
      if (dmgType) effects.push({ type: 'resistance', value: String(dmgType), operation: 'immunity' });
    }
  }

  // Condition immunities → conditionImmunity.grant rows.
  // [Sub-plan 1H-C 2026-05-28]: Reader gap CLOSED.
  //   effectCalculator.getConditionImmunities(character) + isImmuneToCondition(character, name)
  //   now query getEffectsForStat('conditionImmunity', ...). Shape emitted here is live.
  //   Application wiring (before applyCondition in App.jsx) is deferred to Sub-plan 1H-C-i.
  // [BREADCRUMB Rule 25 — Sub-plan 1H-C-i]: Consumer-application site in App.jsx
  //   (applyCondition / toggleCondition flow) not yet wired. Once done, call
  //   effectCalculator.isImmuneToCondition(playerStats, conditionName) before adding
  //   the condition to playerStats.conditions.
  if (Array.isArray(form.conditionImmunities)) {
    for (const cond of form.conditionImmunities) {
      if (cond) effects.push({ type: 'conditionImmunity', target: String(cond), operation: 'grant' });
    }
  }

  // Senses
  const senseFields = [
    { field: 'darkvision', target: 'darkvision' },
    { field: 'blindsight', target: 'blindsight' },
    { field: 'tremorsense', target: 'tremorsense' },
    { field: 'truesight', target: 'truesight' },
  ];
  for (const { field, target } of senseFields) {
    const range = Number(form[field]);
    if (Number.isFinite(range) && range > 0) {
      effects.push({ type: 'sense', target, value: range, operation: 'grant' });
    }
  }

  // Speed bonuses
  const speedFields = [
    { field: 'speedBonus', target: 'walk' },
    { field: 'flyingSpeed', target: 'fly' },
    { field: 'swimmingSpeed', target: 'swim' },
  ];
  for (const { field, target } of speedFields) {
    const v = Number(form[field]);
    if (Number.isFinite(v) && v !== 0) {
      effects.push({ type: 'speed', target, value: v, operation: 'add' });
    }
  }

  // [SUB-PLAN 1H-H Stage 1 — 2026-05-29]: Speed multiply → speed.multiply row
  //   Boots of Speed: doubles walking speed while activated.
  //   Consumer: effectCalculator.js:4940 speedMultiplier path reads
  //   { type:'speed', operation:'multiply', value:2 } via effectRegistry.
  if (form.speedMultiply !== undefined && form.speedMultiply !== null && form.speedMultiply !== '') {
    const v = Number(form.speedMultiply);
    if (Number.isFinite(v) && v > 0) {
      effects.push({ type: 'speed', operation: 'multiply', value: v });
    }
  }

  // [SUB-PLAN 1H-H-cond Phase 1 — 2026-05-29]: Speed minimum → speed.minimum row.
  //   'minimum' is a FLOOR operation (getEffectiveValue applies Math.max(base, N)),
  //   NOT a 'set' (which unconditionally overrides). Pre-Stage-1 shape was
  //   {operation:'set', condition:'minimum'} — the condition string was inert
  //   (always-pass) and the set override was wrong (made 40ft speed → 30ft).
  //   Fix: emit operation:'minimum'; effectRegistry.getEffectiveValue case 'minimum'
  //   applies Math.max(value, highestMinimum) AFTER the SET override.
  if (form.speedMinimum !== undefined && form.speedMinimum !== null && form.speedMinimum !== '') {
    const v = Number(form.speedMinimum);
    if (Number.isFinite(v) && v > 0) {
      effects.push({ type: 'speed', operation: 'minimum', value: v });
    }
  }

  // [SUB-PLAN 1K — 2026-05-27]: HP bonus → hp.add row
  if (form.bonusHP !== undefined && form.bonusHP !== null && form.bonusHP !== '') {
    const v = Number(form.bonusHP);
    if (Number.isFinite(v) && v !== 0) {
      effects.push({ type: 'hp', operation: 'add', value: v });
    }
  }

  // [SUB-PLAN 1K — 2026-05-27]: AC bonus → ac.add row
  if (form.acBonus !== undefined && form.acBonus !== null && form.acBonus !== '') {
    const v = Number(form.acBonus);
    if (Number.isFinite(v) && v !== 0) {
      effects.push({ type: 'ac', operation: 'add', value: v });
    }
  }

  // [SUB-PLAN 1K — 2026-05-27]: Initiative bonus → initiative.add row
  if (form.initiativeBonus !== undefined && form.initiativeBonus !== null && form.initiativeBonus !== '') {
    const v = Number(form.initiativeBonus);
    if (Number.isFinite(v) && v !== 0) {
      effects.push({ type: 'initiative', operation: 'add', value: v });
    }
  }

  // [SUB-PLAN 1K — 2026-05-27]: Saves → one row per non-zero entry.
  //   NOTE: DO NOT write to item.statModifications.saves (S4 keyed-object).
  //   These rows write to canonical S1 effects[] only.
  //
  //   INDEXING CONVENTION (discovered from _getStatKey + effectCalculator reader):
  //   - effectRegistry._getStatKey maps { type:'save', target:'wis' } → 'save:wis'
  //   - effectCalculator.calculateSavingThrow queries getEffectsForStat('save:wisdom', ...)
  //     i.e. full ability name with colon prefix
  //   - Other emitters (srdClassFeatures, feats) use type: 'save:wisdom' directly
  //     so _getStatKey just returns the type string as-is: 'save:wisdom'
  //   Pattern: emit { type: 'save:${fullAbility}', operation: 'add', value } using
  //   the FULL ability name (matching effectCalculator's query key).
  //   'all' is an exception: no single effectRegistry key covers all saves
  //   (effectCalculator has no getEffectsForStat('save:all') call). Emit 6 rows.
  if (form.saves && typeof form.saves === 'object') {
    const FULL_ABILITIES = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];
    const allBonus = Number(form.saves.all);
    const hasAllBonus = Number.isFinite(allBonus) && allBonus !== 0;
    for (const ability of FULL_ABILITIES) {
      const perAbility = Number(form.saves[ability]);
      const perAbilityBonus = Number.isFinite(perAbility) && perAbility !== 0 ? perAbility : 0;
      const total = perAbilityBonus + (hasAllBonus ? allBonus : 0);
      if (total !== 0) {
        effects.push({ type: `save:${ability}`, operation: 'add', value: total });
      }
    }
  }

  // [SUB-PLAN 1A — 2026-05-19]: Stat modifications. Canonical shape is
  //   `form.statModifications: [{ id, target, value }]`. Legacy single-row
  //   (`form.statBonus + form.statValue`) is migrated by hydrateFromTemplate
  //   in CustomItemCreator and not emitted on new saves, but a freshly-edited
  //   BASE_ITEMS row that bypasses the creator still uses the legacy fields.
  //   This helper accepts both: array path iterates rows; legacy fallback
  //   fires only when no array rows exist.
  // [SUB-PLAN 1H-H-maxcap-authoring — 2026-05-30]: emitAbilityRow now accepts an optional
  // `max` parameter. When provided and numeric, it is spread onto the emitted effect so the
  // effectRegistry ceiling (getEffectiveValue lowestMaximum) can clamp the result.
  // Only meaningful for 'add' operations — 'set' and 'subtract'-converted-to-add rows
  // with a max would be unusual game-design, but the registry handles them defensively.
  function emitAbilityRow(target, value, maxCeiling) {
    const m = String(target || '').match(/^(set|add|subtract)(.+)$/i);
    if (!m) return null;
    const op = m[1].toLowerCase();
    const abilityRaw = m[2].toLowerCase();
    const v = Number(value);
    if (!Number.isFinite(v)) return null;
    const row = {
      type: 'ability',
      target: abilityRaw === 'all' ? 'all' : abilityRaw.slice(0, 3),
      operation: op === 'subtract' ? 'add' : op,
      value: op === 'subtract' ? -Math.abs(v) : v,
    };
    // Spread max ceiling only for 'add' operations (after subtract→add conversion)
    // and only when the value is a valid finite number (null / undefined = no ceiling).
    if (row.operation === 'add' && typeof maxCeiling === 'number' && isFinite(maxCeiling)) {
      row.max = maxCeiling;
    }
    return row;
  }
  if (Array.isArray(form.statModifications) && form.statModifications.length > 0) {
    for (const sm of form.statModifications) {
      if (!sm) continue;
      const row = emitAbilityRow(sm.target, sm.value, sm.max);
      if (row) effects.push(row);
    }
  } else if (form.statBonus && form.statValue !== '' && form.statValue !== null && form.statValue !== undefined) {
    const row = emitAbilityRow(form.statBonus, form.statValue);
    if (row) effects.push(row);
  }

  // Advantage-on rolls (skill / save check advantages declared in form)
  if (Array.isArray(form.advantageOn)) {
    for (const target of form.advantageOn) {
      if (target) effects.push({ type: 'advantage', target: String(target), operation: 'advantage' });
    }
  }

  // -------------------------------------------------------------------------
  // [SUB-PLAN 1H-G Stage 1 — 2026-05-29]: Consumable use-effects.
  //   These effect types are TRANSIENT — applied on item use (drink), NOT via
  //   effectRegistry passive pipeline. They land in item.effects[] and are read
  //   by potionEffectApply.buildAppliedPotionEffect + drinkPotion handler.
  //
  //   Shapes must EXACTLY match trace (b) in:
  //   .claude/findings/2026-05-29-datamodel-consumable-use-effects-p7g3.md
  //   No consumer change required — runtime is already proven by sub-plans
  //   1I-c-h, 1U-impl-H, 1Q-F-5a/5b (condition + tempHP + healing + damage).
  //
  // [SUB-PLAN 1H-G Stage 2 — 2026-05-29]: feature / breathWeapon / damageReduction emitters.
  //   Runtime branches added to potionEffectApply.buildAppliedPotionEffect same commit.
  //   drinkPotionLoop catch-all handles all non-null QB entries — no loop change needed.
  // -------------------------------------------------------------------------

  // healing → { type:'healing', operation:'roll', value:'<dice>' }
  // UI field: form.healingRoll (string, e.g. '2d4+2')
  if (form.healingRoll != null && String(form.healingRoll).trim() !== '') {
    effects.push({ type: 'healing', operation: 'roll', value: String(form.healingRoll).trim() });
  }

  // tempHP → { type:'tempHP', operation:'set', value:<n> }
  // UI field: form.tempHP (number)
  if (form.tempHP !== undefined && form.tempHP !== null && form.tempHP !== '') {
    const v = Number(form.tempHP);
    if (Number.isFinite(v) && v > 0) {
      effects.push({ type: 'tempHP', operation: 'set', value: v });
    }
  }

  // condition grant → { type:'condition', target:'<conditionKey>', operation:'grant',
  //                      saveDC?, saveAbility?, recurringDamage?, damageType? }
  // UI field: form.grantConditions (array of objects or plain strings)
  //   Each row: { conditionKey, saveDC?, saveAbility?, recurringDamage?, damageType? }
  //   or just a plain string conditionKey (simple case: no save).
  if (Array.isArray(form.grantConditions)) {
    for (const row of form.grantConditions) {
      if (!row) continue;
      if (typeof row === 'string') {
        if (row.trim()) effects.push({ type: 'condition', target: row.trim(), operation: 'grant' });
      } else if (typeof row === 'object') {
        const conditionKey = String(row.conditionKey || '').trim();
        if (!conditionKey) continue;
        const entry = { type: 'condition', target: conditionKey, operation: 'grant' };
        if (row.saveDC !== undefined && row.saveDC !== null && row.saveDC !== '') {
          const dc = Number(row.saveDC);
          if (Number.isFinite(dc) && dc > 0) entry.saveDC = dc;
        }
        if (row.saveAbility) entry.saveAbility = String(row.saveAbility);
        if (row.recurringDamage) entry.recurringDamage = String(row.recurringDamage);
        if (row.damageType) entry.damageType = String(row.damageType);
        effects.push(entry);
      }
    }
  }

  // damage → { type:'damage', operation:'roll', value:'<dice>', damageType:'<type>' }
  // UI field: form.useEffectDamage (object: { value, damageType })
  if (form.useEffectDamage && typeof form.useEffectDamage === 'object') {
    const diceStr = String(form.useEffectDamage.value || '').trim();
    if (diceStr) {
      const entry = { type: 'damage', operation: 'roll', value: diceStr };
      if (form.useEffectDamage.damageType) entry.damageType = String(form.useEffectDamage.damageType);
      effects.push(entry);
    }
  }

  // -------------------------------------------------------------------------
  // [SUB-PLAN 1H-G Stage 2 — 2026-05-29]: Runtime-extension use-effect emitters.
  //   Three new types whose runtime branches land in potionEffectApply (same commit).
  //   Shapes EXACTLY match trace (b): .claude/findings/2026-05-29-datamodel-consumable-use-effects-p7g3.md
  // -------------------------------------------------------------------------

  // feature → { type:'feature', target:'climbSpeed'|'flySpeed'|'waterBreathing',
  //              operation:'set'|'grant'|'equal_to_walk_speed', value? }
  // UI field: form.featureGrants (array of objects: { target, operation, value? })
  // Example: [{ target:'climbSpeed', operation:'set', value:'walk' }]
  //          [{ target:'waterBreathing', operation:'grant' }]
  //          [{ target:'flySpeed', operation:'equal_to_walk_speed' }]
  if (Array.isArray(form.featureGrants)) {
    for (const row of form.featureGrants) {
      if (!row) continue;
      const target = String(row.target || '').trim();
      const operation = String(row.operation || 'grant').trim();
      if (!target) continue;
      const entry = { type: 'feature', target, operation };
      if (row.value !== undefined && row.value !== null && row.value !== '') {
        entry.value = row.value;
      }
      effects.push(entry);
    }
  }

  // breathWeapon → { type:'breathWeapon', operation:'roll', value:'<dice>',
  //                   damageType, saveDC, saveAbility, uses, duration }
  // UI field: form.breathWeapon (object: { value, damageType, saveDC, saveAbility, uses })
  if (form.breathWeapon && typeof form.breathWeapon === 'object') {
    const diceStr = String(form.breathWeapon.value || '').trim();
    if (diceStr) {
      const entry = { type: 'breathWeapon', operation: 'roll', value: diceStr };
      if (form.breathWeapon.damageType) entry.damageType = String(form.breathWeapon.damageType);
      if (form.breathWeapon.saveDC !== undefined && form.breathWeapon.saveDC !== null && form.breathWeapon.saveDC !== '') {
        const dc = Number(form.breathWeapon.saveDC);
        if (Number.isFinite(dc) && dc > 0) entry.saveDC = dc;
      }
      if (form.breathWeapon.saveAbility) entry.saveAbility = String(form.breathWeapon.saveAbility);
      if (form.breathWeapon.uses !== undefined && form.breathWeapon.uses !== null && form.breathWeapon.uses !== '') {
        const u = Number(form.breathWeapon.uses);
        if (Number.isFinite(u) && u > 0) entry.uses = u;
      }
      effects.push(entry);
    }
  }

  // damageReduction → { type:'damageReduction', operation:'all' }
  // UI field: form.damageReduction (string: 'all')
  if (form.damageReduction === 'all') {
    effects.push({ type: 'damageReduction', operation: 'all' });
  }

  return effects;
}
